import React, { useEffect, useRef, useState } from 'react';
import { gsap } from '../lib/gsap';
import { copy, Language } from '../content/i18n';

interface SubTrailerProps {
  onPlay: () => void;
  language: Language;
}

const SubTrailer: React.FC<SubTrailerProps> = ({ onPlay, language }) => {
  const rootRef = useRef<HTMLElement>(null);
  const previewRef = useRef<HTMLVideoElement>(null);
  const [hovered, setHovered] = useState(false);
  const t = copy[language].subTrailer;

  useEffect(() => {
    const ctx = gsap.context(() => {
      const reveals = rootRef.current?.querySelectorAll('[data-reveal]') ?? [];
      reveals.forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 24 },
          {
            opacity: 1,
            y: 0,
            duration: 1.0,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: el,
              start: 'top 88%',
              toggleActions: 'play none none none',
            },
          }
        );
      });
    }, rootRef);
    return () => ctx.revert();
  }, []);

  const onEnter = () => {
    setHovered(true);
    previewRef.current?.play?.().catch(() => {});
  };
  const onLeave = () => {
    setHovered(false);
    previewRef.current?.pause?.();
  };

  return (
    <section
      ref={rootRef}
      id="taroh-method"
      style={{
        background: '#060606',
        padding: 'clamp(80px,10vw,140px) clamp(24px,6vw,96px)',
      }}
    >
      {/* Section header */}
      <div data-reveal style={{ textAlign: 'center', marginBottom: 56 }}>
        {/* <p
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '0.58rem',
            letterSpacing: '0.48em',
            color: '#c49428',
            textTransform: 'uppercase',
            fontWeight: 500,
            marginBottom: 16,
          }}
        >
          Exclusive Preview
        </p> */}
        <h2
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(2rem, 5vw, 4rem)',
            fontWeight: 300,
            color: '#fff',
            letterSpacing: '0.06em',
            margin: 0,
          }}
        >
          {t.title}
        </h2>
        <p
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '0.68rem',
            color: '#6a6258',
            letterSpacing: '0.12em',
            marginTop: 16,
          }}
        >
          {t.description}
        </p>
      </div>

      {/* Video card */}
      <div data-reveal style={{ maxWidth: 860, margin: '0 auto' }}>
        <div
          role="button"
          tabIndex={0}
          aria-label={t.playAria}
          onClick={onPlay}
          onKeyDown={(e) => e.key === 'Enter' && onPlay()}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
          style={{
            position: 'relative',
            aspectRatio: '16/9',
            overflow: 'hidden',
            cursor: 'pointer',
            borderTop: '1px solid rgba(196,148,40,0.18)',
            borderBottom: '1px solid rgba(196,148,40,0.18)',
            boxShadow: hovered
              ? '0 24px 80px rgba(0,0,0,0.7), 0 0 60px rgba(196,148,40,0.18)'
              : '0 16px 48px rgba(0,0,0,0.5)',
            transition: 'box-shadow 0.4s ease',
          }}
        >
          {/* Poster */}
          <img
            src="/plan/taroh.jpg"
            alt=""
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center 15%',
              filter: 'brightness(0.55) saturate(0.7)',
              transition: 'opacity 0.5s ease',
              opacity: hovered ? 0 : 1,
            }}
          />
          {/* Preview video */}
          <video
            ref={previewRef}
            src="/plan/taroh_method_preview.mp4"
            muted
            loop
            playsInline
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'opacity 0.5s ease',
              opacity: hovered ? 1 : 0,
            }}
          />
          {/* Gradient overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.65) 100%)',
              transition: 'opacity 0.4s ease',
              opacity: hovered ? 0.5 : 1,
            }}
          />
          {/* Amber border on hover */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              border: '1px solid rgba(196,148,40,0.55)',
              transition: 'opacity 0.4s ease',
              opacity: hovered ? 1 : 0,
            }}
          />
          {/* Play button */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: 68,
                height: 68,
                borderRadius: '50%',
                border: '1.5px solid rgba(196,148,40,0.75)',
                background: hovered ? 'rgba(196,148,40,0.18)' : 'rgba(0,0,0,0.45)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: hovered ? 'scale(1.1)' : 'scale(1)',
                transition: 'transform 0.35s ease, background 0.35s ease',
              }}
            >
              <svg
                width="16"
                height="18"
                viewBox="0 0 16 18"
                fill="#c49428"
                aria-hidden="true"
                style={{ marginLeft: 3 }}
              >
                <path d="M0 0l16 9L0 18z" />
              </svg>
            </div>
          </div>
          {/* Bottom labels */}
          <div
            style={{
              position: 'absolute',
              bottom: 20,
              left: 24,
              right: 24,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            {/* <span
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '0.58rem',
                letterSpacing: '0.3em',
                color: 'rgba(255,255,255,0.7)',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              {t.footerLabel}
            </span> */}
            <span
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '0.52rem',
                letterSpacing: '0.2em',
                color: hovered ? '#c49428' : 'rgba(196,148,40,0.45)',
                textTransform: 'uppercase',
                transition: 'color 0.3s ease',
              }}
            >
              {hovered ? t.clickToPlay : null}
            </span>
          </div>
        </div>
      </div>

    
    </section>
  );
};

export default SubTrailer;
